import { Box, Chip, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import React from "react";

import { monospaceStyle } from "../../theme";
import { ProductCard } from "../../types/productPlane";
import CompareToggle from "../Compare/CompareToggle";
import { OrganizationChip } from "../common/OrganizationChip";
import CapabilityChips from "./CapabilityChips";
import {
  UNNAMED_ORGANIZATION,
  makerLine,
  makerRole,
  productLevelAndStatus,
  productName,
} from "./productFields";

export interface ProductResultCardProps {
  product: ProductCard;
  // Opens the product. Without it the card is plain text: nothing on it
  // looks clickable.
  onOpen?: (product: ProductCard) => void;
  // Whether this product is the one open beside the list; it is marked
  open?: boolean;
}

// Who makes the product, in the room the second line of a card leaves: a
// chip for each maker and developer with the flag of its home country and
// its part, then the count of the rest. The chips are cut short rather than
// wrapped, so the card keeps its height; the line in words is its label and
// its tooltip. Without a maker or developer on record it only fills the
// room.
const ProductMakers: React.FC<{ product: ProductCard }> = ({ product }) => {
  if (product.makers.length === 0) {
    return <Box sx={{ flex: 1 }} />;
  }
  return (
    <Box
      aria-label={makerLine(product)}
      title={makerLine(product)}
      data-testid="product-makers"
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        overflow: "hidden",
      }}
    >
      {product.makers.map((maker) => (
        <OrganizationChip
          key={maker.organization.id}
          name={maker.organization.name ?? UNNAMED_ORGANIZATION}
          country={maker.country}
          role={makerRole(maker)}
          sx={{ minWidth: 0 }}
        />
      ))}
      {product.more_makers > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          +{product.more_makers} more
        </Typography>
      )}
    </Box>
  );
};

// One product of a result list: name and number of sources; category, level,
// status, who makes it and the date of the latest source; up to three
// capabilities.
const ProductResultCard: React.FC<ProductResultCardProps> = ({
  product,
  onOpen,
  open = false,
}) => {
  const levelAndStatus = productLevelAndStatus(product);

  const openProps = onOpen
    ? {
        tabIndex: 0,
        onClick: () => onOpen(product),
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter") {
            onOpen(product);
          }
        },
      }
    : {};

  return (
    <Box
      component="article"
      aria-label={productName(product)}
      aria-current={open ? "true" : undefined}
      {...openProps}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1.75,
        px: 2,
        backgroundColor: "background.raised",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        ...(onOpen
          ? { cursor: "pointer", "&:hover": { borderColor: "text.disabled" } }
          : {}),
        // The open card carries one accent ring in place of its border
        ...(open
          ? {
              borderColor: "transparent",
              boxShadow: (theme: Theme) =>
                `0 0 0 1px ${theme.palette.accent.main}`,
              "&:hover": { borderColor: "transparent" },
            }
          : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 2 }}>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere",
            color: product.name ? "text.primary" : "text.secondary",
            fontStyle: product.name ? "normal" : "italic",
          }}
        >
          {productName(product)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ...monospaceStyle, whiteSpace: "nowrap" }}
        >
          {product.source_count} src
        </Typography>
        {product.id && <CompareToggle id={product.id} name={product.name} />}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {product.category && (
          <Chip label={product.category.name} size="small" variant="outlined" />
        )}
        {levelAndStatus && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {levelAndStatus}
          </Typography>
        )}
        <ProductMakers product={product} />
        {product.latest_source && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ ...monospaceStyle, whiteSpace: "nowrap" }}
          >
            {product.latest_source}
          </Typography>
        )}
      </Box>

      <CapabilityChips product={product} />
    </Box>
  );
};

export default ProductResultCard;
